import React, { memo, useCallback, useState } from 'react';

import { useFeatureToggle } from '@flopflip/react-redux';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import SearchInput from 'app/shared/components/common/SearchInput';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';
import { KEY_CODES } from 'constants/keyboard';

import { ProgramsAndPricingTestIds } from '../../ProgramsAndPricing.testIds';
import { formControlStyles } from '../ProgramsFilterPanel/ProgramsFilterPanel.styles';

import { DEFAULT_VALID_QUERY_LENGTH } from './ProgramsTableToolbar.constants';
import {
  filterTogglesStyles,
  helperTextStyles,
  searchInputStyles,
} from './ProgramsTableToolbar.styles';
import RateSheetName from './RateSheetName';

const ProgramsTableToolbar = ({
  onSearch,
  isLoading = false,
  children,
  isSearchOnChange = true,
  validQueryLength = DEFAULT_VALID_QUERY_LENGTH,
  setTempRateSheetId,
  rateSheetId,
  isFromPropertyAndLoan,
  pendingChanges,
  isHideNonEligible,
  setIsHideNonEligible,
  isShowExpiredPricing = false,
  setIsShowExpiredPricing = _.noop,
}) => {
  const isExpiredPricingSaveEnabled = useFeatureToggle(FeatureFlag.EnableExpiredPricingSave);

  const [searchValue, setSearchValue] = useState('');
  const [isSearchPerformed, setIsSearchPerformed] = useState(true);

  const isValidQueryLength = useCallback(
    query => {
      return !query.length || query.trim().length >= validQueryLength;
    },
    [validQueryLength],
  );

  const onChange = useCallback(
    ({ target }) => {
      const { value } = target;

      setSearchValue(value);

      if (isSearchOnChange && isValidQueryLength) {
        onSearch?.(value);
        setIsSearchPerformed(true);

        return;
      }

      setIsSearchPerformed(false);
    },
    [isSearchOnChange, isValidQueryLength, onSearch],
  );

  const onEnterPress = useCallback(
    ({ target: { value }, charCode }) => {
      if (isSearchOnChange) {
        return;
      }

      if (charCode === KEY_CODES.ENTER && isValidQueryLength) {
        onSearch?.(value);
        setIsSearchPerformed(true);
      }
    },
    [onSearch, isValidQueryLength, isSearchOnChange],
  );

  const onShowExpiredPricingChange = useCallback(
    e => setIsShowExpiredPricing(e.target.checked),
    [setIsShowExpiredPricing],
  );

  return (
    <Stack className="gap-16 w-full" data-testid={ProgramsAndPricingTestIds.PROGRAMS_TABLE_TOOLBAR}>
      {children}

      {onSearch && (
        <Box className="flex justify-between items-center gap-4">
          <div className="flex items-center flex-1">
            {onSearch && (
              <SearchInput
                focusOnType
                isFilled
                isWithIcon
                className="mr-24"
                disabled={isLoading}
                placeholder="Search"
                rightSideContent={
                  !isSearchPerformed && isValidQueryLength(searchValue) && !isSearchOnChange ? (
                    <Typography style={helperTextStyles} variant="regular14">
                      Press &quot;Enter&quot;
                    </Typography>
                  ) : null
                }
                style={searchInputStyles}
                value={searchValue}
                onChange={onChange}
                onKeyPress={onEnterPress}
              />
            )}

            <Box sx={filterTogglesStyles}>
              <FormControl disabled={isLoading} sx={formControlStyles}>
                <FormLabel component="legend">
                  <Typography color="text.secondary" variant="regular14">
                    Hide ineligible programs:
                  </Typography>
                </FormLabel>
                <FormControlLabel
                  checked={isHideNonEligible}
                  className="switch-label"
                  control={<Switch />}
                  data-testid={ProgramsAndPricingTestIds.HIDE_INELIGIBLE_SWITCH}
                  // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop
                  onChange={e => setIsHideNonEligible(e.target.checked)}
                />
              </FormControl>

              {isExpiredPricingSaveEnabled && (
                <FormControl disabled={isLoading} sx={formControlStyles}>
                  <FormLabel component="legend">
                    <Typography color="text.secondary" variant="regular14">
                      Show expired pricing:
                    </Typography>
                  </FormLabel>
                  <FormControlLabel
                    checked={isShowExpiredPricing}
                    className="switch-label"
                    control={<Switch />}
                    data-testid={ProgramsAndPricingTestIds.SHOW_EXPIRED_PRICING_SWITCH}
                    onChange={onShowExpiredPricingChange}
                  />
                </FormControl>
              )}
            </Box>
          </div>

          <RateSheetName
            isFromPropertyAndLoan={isFromPropertyAndLoan}
            pendingChanges={pendingChanges}
            rateSheetId={rateSheetId}
            setTempRateSheetId={setTempRateSheetId}
          />
        </Box>
      )}
    </Stack>
  );
};

ProgramsTableToolbar.propTypes = {
  isFromPropertyAndLoan: PropTypes.bool.isRequired,
  isHideNonEligible: PropTypes.bool.isRequired,
  setIsHideNonEligible: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isSearchOnChange: PropTypes.bool,
  isShowExpiredPricing: PropTypes.bool,
  validQueryLength: PropTypes.number,
  pendingChanges: PropTypes.object,
  children: PropTypes.node,
  rateSheetId: PropTypes.string,
  setIsShowExpiredPricing: PropTypes.func,
  setTempRateSheetId: PropTypes.func,
  onSearch: PropTypes.func,
};

export default memo(ProgramsTableToolbar);
